import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageContent } from '../components/layout/PageContent';
import { federationApi, rankingFederativoApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { SecaoFormulario, Dica, BarraAcoes } from '../components/common/Formulario';
import {
  Settings2,
  Loader2,
  Save,
  Building2,
  Medal,
  Trophy,
  Users,
  Copy,
  CalendarPlus,
  ShieldCheck,
  History,
  Swords,
} from 'lucide-react';
import { toast } from 'sonner';
import { EstadoVazio } from '../components/common/Estados';

/**
 * FASE C — Configuração/UI das REGRAS do Ranking Federativo.
 *
 * A Federação/Liga é a DONA das regras (FASE B): esta tela apenas edita a
 * regra versionada (PUT cria v1 ou versiona vN+1) — snapshots históricos
 * nunca são afetados. Peso do campeonato é BÔNUS ADITIVO (+1/+3/+5 padrão) e,
 * desde o Bloco B, é pago pela PRÓPRIA configuração da federação (não depende
 * da participação estar ligada). Participação e o bônus de performance são
 * DOIS valores INDEPENDENTES (por pontos e por finalização) que podem ser
 * configurados ao mesmo tempo; 0 desativa o bônus. Peso e participação exigem
 * pelo menos 1 luta efetiva do atleta.
 */

const EMPTY_CONFIG = {
  medal_points: { gold: 10, silver: 5, bronze: 3, second_bronze: 0 },
  participation_points: 0,
  // null = regra nova, SEM modalidade excludente: os dois bônus abaixo
  // (por pontos e por finalização) são independentes e aplicados por TIPO DE
  // VITÓRIA (Etapa 2) — pontos → X, finalização → Y, derrota → 0.
  performance_mode: null,
  per_fight_points: 0,
  submission_bonus_points: 0,
  // Etapa 3 — a agregação do Ranking Federativo é SEMPRE aditiva: o ranking
  // anual é a SOMA dos totais por campeonato (sem média/divisão). O campo é
  // gravado como "direct" apenas como marcação auditável da regra vigente.
  performance_aggregation: 'direct',
  include_walkovers: true,
  weight_points: { low: 1, medium: 3, high: 5 },
  // FASE 4 — as EQUIPES são SEMPRE por medalha. O campo continua no schema
  // (marcação auditável, como `performance_aggregation`), mas não é mais uma
  // escolha da federação: nenhum cálculo lê este valor.
  team_points_source: 'medals_only',
};

function NumberField({ label, value, onChange, max, hint, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[#A1A1AA] text-xs">{label}</Label>
      <Input
        type="number"
        min="0"
        max={max != null ? String(max) : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="bg-[#1A1A1A] border-[#27272A] text-white h-9"
      />
      {hint && <Dica icone={false}>{hint}</Dica>}
    </div>
  );
}

function num(value) {
  const n = parseInt(value, 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

// Bloco A (S7) — tons do painel de consistência (diagnóstico).
const NOTE_TONES = {
  ok: 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300',
  warn: 'bg-amber-500/10 border-amber-500/40 text-amber-300',
  info: 'bg-[#1A1A1A] border-[#27272A] text-[#A1A1AA]',
};

const WEIGHT_LABELS = { low: 'Baixo', medium: 'Médio', high: 'Alto' };

export default function FederativoRulesPage() {
  const { user } = useAuth();
  const isAllowed = ['super_admin', 'federation'].includes(user?.role);

  const [federations, setFederations] = useState([]);
  const [federationId, setFederationId] = useState('');
  const [ruleMeta, setRuleMeta] = useState(null); // { version, exists, updated_at, copied_from_federation_id }
  const [form, setForm] = useState(EMPTY_CONFIG);
  // FASE 4 — valor de `team_points_source` GRAVADO na regra (histórico). A
  // agregação das equipes é sempre por medalha; guardamos o valor apenas para
  // mostrar, em modo leitura, o que ficou congelado nas regras antigas.
  const [storedTeamSource, setStoredTeamSource] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Temporadas
  const [seasons, setSeasons] = useState([]);
  const [seasonForm, setSeasonForm] = useState({ season: '', year_start: '', year_end: '' });
  const [creatingSeason, setCreatingSeason] = useState(false);

  // Copiar regra
  const [copySource, setCopySource] = useState('');
  const [copying, setCopying] = useState(false);

  const [searchParams] = useSearchParams();

  useEffect(() => {
    const initialFederationId = searchParams.get('federationId') || '';
    federationApi
      .list()
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : [];
        setFederations(list);
        if (list.length > 0) {
          // Use federationId from URL if valid, otherwise fallback to first federation
          const validId = initialFederationId && list.some((f) => f._id === initialFederationId)
            ? initialFederationId
            : list[0]._id;
          setFederationId((prev) => prev || validId);
        }
      })
      .catch(() => toast.error('Erro ao carregar federações'));
  }, []);

  const loadRule = useCallback(async (fid) => {
    if (!fid) return;
    setLoading(true);
    try {
      const { data } = await rankingFederativoApi.getRule(fid);
      const cfg = data?.config || EMPTY_CONFIG;
      setForm({
        medal_points: { ...EMPTY_CONFIG.medal_points, ...(cfg.medal_points || {}) },
        participation_points: cfg.participation_points ?? 0,
        performance_mode: cfg.performance_mode || null,
        per_fight_points: cfg.per_fight_points ?? 0,
        submission_bonus_points: cfg.submission_bonus_points ?? 0,
        // Etapa 3 — a agregação vigente é sempre ADITIVA. Regras antigas podem
        // ter "sum_divided" gravado, mas nenhum cálculo a aplica; a tela abre
        // sempre no comportamento real (soma dos campeonatos).
        performance_aggregation: 'direct',
        include_walkovers: cfg.include_walkovers !== false,
        weight_points: { ...EMPTY_CONFIG.weight_points, ...(cfg.weight_points || {}) },
        // FASE 4 — a agregação das equipes é SEMPRE por medalha: a tela abre no
        // comportamento real. O valor gravado (inclusive volume, em regras
        // antigas) é guardado só para exibição informativa — não é usado.
        team_points_source: 'medals_only',
      });
      setStoredTeamSource(cfg.team_points_source || null);
      setRuleMeta({
        version: data?.version ?? null,
        exists: !!data?.exists,
        updated_at: data?.updated_at || null,
        copied_from_federation_id: data?.copied_from_federation_id || null,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao carregar regra da federação');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSeasons = useCallback(async (fid) => {
    if (!fid) return;
    try {
      const { data } = await rankingFederativoApi.listSeasons(fid);
      setSeasons(Array.isArray(data) ? data : []);
    } catch {
      setSeasons([]);
    }
  }, []);

  useEffect(() => {
    if (federationId) {
      loadRule(federationId);
      loadSeasons(federationId);
    }
  }, [federationId, loadRule, loadSeasons]);

  const handleSave = async () => {
    if (!federationId) return;
    setSaving(true);
    try {
      const payload = {
        medal_points: {
          gold: num(form.medal_points.gold),
          silver: num(form.medal_points.silver),
          bronze: num(form.medal_points.bronze),
          second_bronze: num(form.medal_points.second_bronze),
        },
        participation_points: num(form.participation_points),
        // Os DOIS bônus são gravados de forma independente; nenhuma modalidade
        // excludente é enviada (o legado continua sendo LIDO pelo backend).
        performance_mode: null,
        per_fight_points: Math.min(num(form.per_fight_points), 5),
        submission_bonus_points: num(form.submission_bonus_points),
        // Normalizado no salvamento (mesmo padrão de `performance_mode: null`):
        // a agregação do Ranking Federativo é sempre aditiva.
        performance_aggregation: 'direct',
        include_walkovers: !!form.include_walkovers,
        weight_points: {
          low: num(form.weight_points.low),
          medium: num(form.weight_points.medium),
          high: num(form.weight_points.high),
        },
        // FASE 4 — normalizado no salvamento (mesmo padrão de
        // `performance_aggregation`): as equipes são sempre por medalha.
        team_points_source: 'medals_only',
      };
      const { data } = await rankingFederativoApi.updateRule(federationId, payload);
      setRuleMeta({
        version: data?.version ?? null,
        exists: true,
        updated_at: data?.updated_at || null,
        copied_from_federation_id: data?.copied_from_federation_id || null,
      });
      // A marcação da modalidade antiga é removida no salvamento (o backend
      // não a grava mais): o formulário reflete o que acabou de ser salvo.
      setForm((prev) => ({ ...prev, performance_mode: null }));
      toast.success(data?.message || `Regra versionada (v${data?.version ?? '?'})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar regra');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateSeason = async (e) => {
    e.preventDefault();
    if (!federationId || !seasonForm.season) return;
    setCreatingSeason(true);
    try {
      const body = { season: seasonForm.season };
      if (seasonForm.year_start) body.year_start = parseInt(seasonForm.year_start, 10);
      if (seasonForm.year_end) body.year_end = parseInt(seasonForm.year_end, 10);
      await rankingFederativoApi.createSeason(federationId, body);
      toast.success(`Temporada "${seasonForm.season}" criada`);
      setSeasonForm({ season: '', year_start: '', year_end: '' });
      loadSeasons(federationId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar temporada');
    } finally {
      setCreatingSeason(false);
    }
  };

  const handleCopyRule = async () => {
    if (!federationId || !copySource) return;
    if (!window.confirm('Copiar a regra vigente da federação de origem? A cópia é independente (sem referência viva) e versiona a regra desta federação.')) return;
    setCopying(true);
    try {
      await rankingFederativoApi.copyRule(federationId, copySource);
      toast.success('Regra copiada de forma independente');
      loadRule(federationId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao copiar regra');
    } finally {
      setCopying(false);
    }
  };

  // -------------------------------------------------------------------------
  // Bloco A (S7) — Consistência da regra (diagnóstico, SOMENTE LEITURA).
  // Traduz a regra atual para o que ela significa na prática e aponta onde ela
  // pode enganar quem opera. Não bloqueia nada e não altera nenhum valor.
  // -------------------------------------------------------------------------
  const consistencyNotes = (() => {
    const notes = [];
    if (!ruleMeta?.exists) {
      notes.push({
        tone: 'info',
        text: 'Esta federação ainda NÃO salvou uma regra. Os valores exibidos são o padrão do sistema e passam a ser a versão 1 na primeira necessidade (entrada de um campeonato em andamento).',
      });
    }

    const medals = {
      gold: num(form.medal_points.gold),
      silver: num(form.medal_points.silver),
      bronze: num(form.medal_points.bronze),
      second_bronze: num(form.medal_points.second_bronze),
    };
    const medalsTotal = medals.gold + medals.silver + medals.bronze + medals.second_bronze;
    const participation = num(form.participation_points);
    const perFight = Math.min(num(form.per_fight_points), 5);
    const submission = num(form.submission_bonus_points);
    const performanceTotal = perFight + submission;
    const weights = {
      low: num(form.weight_points.low),
      medium: num(form.weight_points.medium),
      high: num(form.weight_points.high),
    };
    const weightTotal = weights.low + weights.medium + weights.high;

    if (medalsTotal === 0 && participation === 0 && performanceTotal === 0 && weightTotal === 0) {
      notes.push({ tone: 'warn', text: 'Todos os pontos estão ZERADOS: com esta regra o ranking não pontua nada — nem a medalha.' });
    } else if (medalsTotal === 0) {
      notes.push({ tone: 'warn', text: 'Nenhum ponto de medalha: ouro, prata, bronze e 2º bronze valem 0. O ranking será decidido por participação, performance e peso.' });
    } else {
      notes.push({ tone: 'ok', text: `Medalha pontua: ouro ${medals.gold} · prata ${medals.silver} · bronze ${medals.bronze} · 2º bronze ${medals.second_bronze}. A medalha é a COLOCAÇÃO na chave — não depende de lutar nem de como venceu (campeão por WO ou por chave de 1 atleta também é ouro).` });
    }

    if (participation === 0 && performanceTotal === 0) {
      notes.push({ tone: 'info', text: 'Sem participação e sem bônus de performance: apenas medalha e peso somam pontos.' });
    } else {
      notes.push({ tone: 'info', text: `Participação = ${participation} ponto(s), pagos UMA vez por atleta por campeonato e somente se o atleta tiver pelo menos 1 luta efetiva (BYE nunca conta; WO só conta se a opção "Considerar WO como luta" estiver marcada).` });
    }

    if (performanceTotal === 0) {
      notes.push({ tone: 'info', text: 'Bônus de performance desativado (X e Y em 0).' });
    } else {
      notes.push({ tone: 'info', text: `Performance por tipo de vitória: vitória por pontos → ${perFight}${perFight === 0 ? ' (não pontua)' : ''}; vitória por finalização → ${submission}${submission === 0 ? ' (não pontua)' : ''}; derrota → 0. A MESMA luta nunca recebe os dois bônus, e WO/desclassificação não geram bônus.` });
    }

    Object.entries(weights).forEach(([level, value]) => {
      if (value === 0) {
        notes.push({ tone: 'warn', text: `Faixa "${WEIGHT_LABELS[level]}" desativada (0): campeonatos classificados como ${WEIGHT_LABELS[level]} não somam bônus de peso.` });
      }
    });

    if (weightTotal > 0) {
      notes.push({
        tone: 'info',
        text: `Bloco B — o bônus de PESO é pago pela PRÓPRIA configuração desta tela (Baixo +${weights.low} · Médio +${weights.medium} · Alto +${weights.high}) e NÃO depende mais de a participação estar ligada: funciona mesmo com participação em 0. Ele exige apenas que o atleta tenha pelo menos 1 luta efetiva e nunca multiplica medalha, participação ou performance. Campeonatos já congelados antes desta mudança continuam com o comportamento antigo.`,
      });
    }

    if (form.performance_mode) {
      notes.push({
        tone: 'warn',
        text: `Esta regra ainda declara a MODALIDADE ANTIGA ("${form.performance_mode}") da fase anterior. Campeonatos com snapshot já congelado continuam aplicando exatamente aquela modalidade (o histórico não é reinterpretado); campeonatos NOVOS usam a fórmula por tipo de vitória — e salvar esta tela remove a marcação antiga.`,
      });
    }

    if (form.team_points_source === 'medals_only') {
      notes.push({ tone: 'info', text: 'Ranking das Equipes: SOMENTE medalha — contam apenas os atletas medalhistas e apenas os pontos de medalha (ordem de ouro, prata, bronze).' });
    }

    notes.push({
      tone: 'ok',
      text: `Ranking anual = SOMA dos campeonatos: cada campeonato vale medalha + participação + performance + peso, e a temporada acumula esses totais. Exemplo: 17 + 9 + 9 = 35 pontos. Não há média, divisão nem arredondamento.`,
    });

    notes.push({ tone: 'info', text: 'Tudo o que está nesta tela vale para campeonatos NOVOS. Campeonatos que já têm snapshot congelado (entraram em andamento antes) não mudam.' });
    return notes;
  })();

  if (!isAllowed) {
    return (
      <PageContent>
        <Card className="bg-[#141414] border-[#27272A] max-w-md mx-auto mt-12">
          <CardContent className="p-8 text-center">
            <ShieldCheck className="w-12 h-12 text-[#71717A] mx-auto mb-3" />
            <h2 className="text-xl font-bold text-white mb-2">Acesso restrito</h2>
            <p className="text-[#A1A1AA] text-sm">As regras do Ranking Federativo são configuradas apenas pela Federação/Liga e pelo Super Admin.</p>
          </CardContent>
        </Card>
      </PageContent>
    );
  }

  const copySourceName = federations.find((f) => f._id === copySource)?.name;
  const copiedFromName = federations.find((f) => f._id === ruleMeta?.copied_from_federation_id)?.name;

  return (
    <PageContent>
      <div className="space-y-6" data-testid="federativo-rules-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-white font-['Chivo'] flex items-center gap-2">
            <Settings2 size={24} className="text-[#D4AF37]" /> Regras do Ranking Federativo
          </h1>
          <p className="text-[#A1A1AA] mt-1 text-sm">
            A Federação/Liga é a dona das regras do seu ranking. Alterações criam uma nova versão — snapshots históricos nunca são afetados.
          </p>
        </div>

        {/* Federation selector */}
        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <label className="text-sm font-bold text-[#A1A1AA] uppercase tracking-wider shrink-0">Federação/Liga:</label>
              <Select value={federationId || undefined} onValueChange={setFederationId}>
                <SelectTrigger data-testid="rules-federation-select" className="bg-[#1A1A1A] border-[#27272A] text-white max-w-md">
                  <SelectValue placeholder="Selecione a federação" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                  {federations.map((f) => (
                    <SelectItem key={f._id} value={f._id} className="text-white hover:bg-[#27272A]">
                      <span className="flex items-center gap-2"><Building2 size={16} className="text-[#D4AF37]" /> {f.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {ruleMeta && (
                <div className="flex flex-wrap items-center gap-2 text-xs sm:ml-auto">
                  {ruleMeta.exists ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37]" data-testid="rule-version-badge">
                      <History size={12} /> Regra vigente: versão {ruleMeta.version}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-500/10 border border-blue-500/30 text-blue-300">
                      Sem regra configurada — salvar cria a versão 1
                    </span>
                  )}
                  {copiedFromName && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#1A1A1A] border border-[#27272A] text-[#71717A]">
                      <Copy size={12} /> Cópia independente de: {copiedFromName}
                    </span>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span></div>
        ) : !federationId ? (
          <Card className="bg-[#141414] border-[#27272A]">
            <CardContent>
              <EstadoVazio icone={Building2} titulo="Selecione uma federação" descricao="Cada federação/Liga possui regras, temporadas e ranking próprios" />
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Rule form */}
            <Card className="bg-[#141414] border-[#27272A]">
              <CardHeader>
                <CardTitle className="text-white font-['Chivo'] flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-[#D4AF37]" /> Pontuação do Ranking
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <SecaoFormulario titulo="Pontos por medalha" icone={Medal}>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <NumberField label="Ouro" value={form.medal_points.gold} onChange={(v) => setForm({ ...form, medal_points: { ...form.medal_points, gold: v } })} testId="rule-medal-gold" />
                    <NumberField label="Prata" value={form.medal_points.silver} onChange={(v) => setForm({ ...form, medal_points: { ...form.medal_points, silver: v } })} testId="rule-medal-silver" />
                    <NumberField label="Bronze" value={form.medal_points.bronze} onChange={(v) => setForm({ ...form, medal_points: { ...form.medal_points, bronze: v } })} testId="rule-medal-bronze" />
                    <NumberField label="2º Bronze" value={form.medal_points.second_bronze} onChange={(v) => setForm({ ...form, medal_points: { ...form.medal_points, second_bronze: v } })} hint="Quando aplicável" testId="rule-medal-second-bronze" />
                  </div>
                </SecaoFormulario>

                <SecaoFormulario titulo="Participação" icone={Users}>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <NumberField
                      label="Pontos de participação"
                      value={form.participation_points}
                      onChange={(v) => setForm({ ...form, participation_points: v })}
                      hint="Concedido por participação efetiva em LUTA (medalha não é requisito). 0 = desativado."
                      testId="rule-participation"
                    />
                  </div>
                </SecaoFormulario>

                <SecaoFormulario titulo="Bônus de performance (por tipo de vitória)" icone={Swords}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <NumberField
                      label="Bônus por vitória por pontos (X)"
                      value={form.per_fight_points}
                      onChange={(v) => setForm({ ...form, per_fight_points: v })}
                      max={5}
                      hint="0 = desativado. Pago ao vencedor quando a vitória é por pontos/vantagens (máx. 5 por luta)."
                      testId="rule-per-fight-points"
                    />
                    <NumberField
                      label="Bônus por vitória por finalização (Y)"
                      value={form.submission_bonus_points}
                      onChange={(v) => setForm({ ...form, submission_bonus_points: v })}
                      hint="0 = desativado. Pago ao vencedor quando a vitória é por finalização."
                      testId="rule-submission-points"
                    />
                    <div className="space-y-1.5">
                      <Label className="text-[#A1A1AA] text-xs">Acúmulo no ranking anual</Label>
                      <div
                        className="px-3 py-2 rounded-lg bg-[#1A1A1A] border border-[#27272A] text-white text-sm"
                        data-testid="rule-performance-aggregation"
                      >
                        Soma dos campeonatos — bônus acumulado (sem média)
                      </div>
                      <Dica icone={false}>
                        O Ranking Federativo SOMA o total de cada campeonato da temporada. Não existe
                        divisão pelo número de campeonatos, média nem arredondamento.
                      </Dica>
                    </div>
                  </div>
                  <Dica icone={false}>
                    Fórmula vigente: <strong className="text-[#A1A1AA]">vitória por pontos → X</strong>,{' '}
                    <strong className="text-[#A1A1AA]">vitória por finalização → Y</strong>,{' '}
                    <strong className="text-[#A1A1AA]">derrota → 0</strong>. Os dois bônus são{' '}
                    <strong className="text-[#A1A1AA]">independentes</strong>, podem ser configurados ao mesmo tempo e
                    0 desativa o bônus — mas a MESMA luta nunca recebe os dois. Vitória por WO ou
                    desclassificação não gera bônus de performance; a medalha e a contagem de luta seguem
                    as regras normais.
                  </Dica>
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="include-walkovers"
                      checked={form.include_walkovers}
                      onCheckedChange={(checked) => setForm({ ...form, include_walkovers: !!checked })}
                      data-testid="rule-include-walkovers"
                      className="border-[#27272A] data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37] mt-0.5"
                    />
                    <div>
                      <Label htmlFor="include-walkovers" className="text-[#A1A1AA] cursor-pointer text-sm">
                        Considerar WO como luta para contagem/participação
                      </Label>
                      <Dica tom="aviso">
                        Desmarcado: WO não conta como luta. BYE nunca conta, independente da configuração. O WO nunca
                        gera bônus de performance — não é vitória por pontos nem por finalização.
                      </Dica>
                    </div>
                  </div>
                </SecaoFormulario>

                <SecaoFormulario titulo="Peso do campeonato (bônus aditivo)" icone={Trophy}>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <NumberField label="BAIXO (LOW)" value={form.weight_points.low} onChange={(v) => setForm({ ...form, weight_points: { ...form.weight_points, low: v } })} hint="Padrão +1 · 0 = desativado" testId="rule-weight-low" />
                    <NumberField label="MÉDIO (MEDIUM)" value={form.weight_points.medium} onChange={(v) => setForm({ ...form, weight_points: { ...form.weight_points, medium: v } })} hint="Padrão +3 · 0 = desativado" testId="rule-weight-medium" />
                    <NumberField label="ALTO (HIGH)" value={form.weight_points.high} onChange={(v) => setForm({ ...form, weight_points: { ...form.weight_points, high: v } })} hint="Padrão +5 · 0 = desativado" testId="rule-weight-high" />
                  </div>
                  <Dica icone={false}>
                    Bônus SOMADO ao total do campeonato (medalha + participação + performance + peso). Nunca é multiplicador. O nome do campeonato não define o peso — a federação classifica cada competição em BAIXO/MÉDIO/ALTO.
                  </Dica>
                </SecaoFormulario>

                <SecaoFormulario titulo="Ranking das Equipes" icone={Users}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      {/* FASE 4 — não existe mais ESCOLHA: a equipe é sempre por
                          medalha. O campo `team_points_source` segue gravado
                          apenas como auditoria (histórico das regras antigas). */}
                      <Label className="text-[#A1A1AA] text-xs">Pontuação das equipes (regra vigente)</Label>
                      <div
                        data-testid="team-aggregation-fixed"
                        className="rounded-lg border border-[#D4AF37]/40 bg-[#D4AF37]/5 px-3 py-2 text-xs text-[#E5C158] font-semibold flex items-center gap-2"
                      >
                        <Users className="w-3.5 h-3.5" /> Sempre por MEDALHA (ouro, prata, bronze, 2º bronze)
                      </div>
                      <Dica icone={false}>
                        A equipe soma <span className="text-[#A1A1AA]">somente os pontos de medalha</span> dos
                        atletas medalhistas (atleta sem medalha não entrega ponto) e a ordem é a de medalha
                        (ouro → prata → bronze). A pontuação da equipe é sempre a agregação dos resultados
                        individuais — nunca uma pontuação paralela.
                      </Dica>
                      <Dica icone={false}>
                        <strong className="text-[#A1A1AA]">As opções antigas não são mais usadas:</strong>{' '}
                        &ldquo;todos os atletas pontuados&rdquo; e &ldquo;somente atletas que lutaram&rdquo;
                        (por pontuação) não entram em nenhum cálculo. Regras e campeonatos antigos podem ter
                        esse valor gravado — ele fica apenas como histórico de auditoria.
                      </Dica>
                      {storedTeamSource && storedTeamSource !== 'medals_only' && (
                        <Dica tom="aviso" icone={false} className="[&>span]:w-full">
                          <span data-testid="team-source-legacy-note">
                            Histórico congelado nesta federação:{' '}
                            <span className="font-mono">{storedTeamSource}</span> (sem efeito no cálculo atual).
                          </span>
                        </Dica>
                      )}
                    </div>
                  </div>
                </SecaoFormulario>

                {/* Ação primária SEMPRE visível: a barra fica fixa no rodapé
                    enquanto o formulário (longo) rola. */}
                <BarraAcoes contexto="cartao">
                  <Button
                    variant="outline"
                    onClick={() => loadRule(federationId)}
                    disabled={loading || saving}
                    className="border-[#27272A] text-[#A1A1AA] hover:text-white"
                  >
                    Descartar alterações
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={saving || loading}
                    data-testid="save-rule-btn"
                    className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
                    Salvar (versionar regra)
                  </Button>
                </BarraAcoes>
              </CardContent>
            </Card>

            {/* Bloco A (S7) — Consistência da regra (diagnóstico, somente leitura) */}
            <Card className="bg-[#141414] border-[#27272A]" data-testid="rule-consistency-panel">
              <CardHeader>
                <CardTitle className="text-white font-['Chivo'] text-lg flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-[#D4AF37]" /> Consistência desta regra (diagnóstico)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {consistencyNotes.map((note, index) => (
                  <p
                    key={index}
                    className={`text-xs rounded-lg border px-3 py-2 leading-relaxed ${NOTE_TONES[note.tone] || NOTE_TONES.info}`}
                  >
                    {note.text}
                  </p>
                ))}
              </CardContent>
            </Card>

            {/* Temporadas */}
            <Card className="bg-[#141414] border-[#27272A]">
              <CardHeader>
                <CardTitle className="text-white font-['Chivo'] text-lg flex items-center gap-2">
                  <CalendarPlus className="w-5 h-5 text-[#D4AF37]" /> Temporadas
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {seasons.length > 0 && (
                  <div className="flex flex-wrap gap-2" data-testid="seasons-list">
                    {seasons.map((s) => (
                      <span key={s._id} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#1A1A1A] border border-[#27272A] text-sm text-white">
                        {s.season}
                        {s.year_start != null && s.year_end != null && (
                          <span className="text-[#71717A] text-xs">({s.year_start}–{s.year_end})</span>
                        )}
                      </span>
                    ))}
                  </div>
                )}
                <form onSubmit={handleCreateSeason} className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
                  <div className="space-y-1.5 flex-1 min-w-[160px]">
                    <Label className="text-[#A1A1AA] text-xs">Temporada</Label>
                    <Input
                      value={seasonForm.season}
                      onChange={(e) => setSeasonForm({ ...seasonForm, season: e.target.value })}
                      placeholder="Ex.: 2026 ou 2026/2027"
                      required
                      data-testid="season-name"
                      className="bg-[#1A1A1A] border-[#27272A] text-white h-9"
                    />
                  </div>
                  <div className="space-y-1.5 w-28">
                    <Label className="text-[#A1A1AA] text-xs">Início</Label>
                    <Input
                      type="number"
                      value={seasonForm.year_start}
                      onChange={(e) => setSeasonForm({ ...seasonForm, year_start: e.target.value })}
                      placeholder="2026"
                      data-testid="season-year-start"
                      className="bg-[#1A1A1A] border-[#27272A] text-white h-9"
                    />
                  </div>
                  <div className="space-y-1.5 w-28">
                    <Label className="text-[#A1A1AA] text-xs">Fim</Label>
                    <Input
                      type="number"
                      value={seasonForm.year_end}
                      onChange={(e) => setSeasonForm({ ...seasonForm, year_end: e.target.value })}
                      placeholder="2027"
                      data-testid="season-year-end"
                      className="bg-[#1A1A1A] border-[#27272A] text-white h-9"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={creatingSeason || !seasonForm.season}
                    className="bg-[#D4AF37] text-black hover:bg-[#E5C158]"
                  >
                    {creatingSeason ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CalendarPlus size={16} className="mr-2" />}
                    Criar temporada
                  </Button>
                </form>
                <Dica icone={false}>
                  Janela de temporada (ex.: 2025–2026) classifica campeonatos pelo ano da data. Sem janela correspondente, o ano do campeonato é usado como temporada.
                </Dica>
              </CardContent>
            </Card>

            {/* Copiar regra de outra federação */}
            <Card className="bg-[#141414] border-[#27272A]">
              <CardHeader>
                <CardTitle className="text-white font-['Chivo'] text-lg flex items-center gap-2">
                  <Copy className="w-5 h-5 text-[#D4AF37]" /> Copiar regra de outra federação
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
                  <div className="space-y-1.5 flex-1 min-w-[220px]">
                    <Label className="text-[#A1A1AA] text-xs">Federação de origem</Label>
                    <Select value={copySource || undefined} onValueChange={setCopySource}>
                      <SelectTrigger data-testid="copy-rule-source" className="bg-[#1A1A1A] border-[#27272A] text-white">
                        <SelectValue placeholder="Selecione a federação de origem" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                        {federations.filter((f) => f._id !== federationId).map((f) => (
                          <SelectItem key={f._id} value={f._id} className="text-white hover:bg-[#27272A]">{f.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={handleCopyRule}
                    disabled={copying || !copySource}
                    variant="outline"
                    className="border-[#27272A] text-[#A1A1AA] hover:text-white"
                  >
                    {copying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Copy size={16} className="mr-2" />}
                    Copiar para esta federação
                  </Button>
                </div>
                <Dica icone={false} className="mt-2">
                  A cópia é INDEPENDENTE: alterações futuras na federação de origem não afetam esta regra (apenas o registro histórico de origem é mantido).
                </Dica>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </PageContent>
  );
}
